import { validateEnumTranslations } from '../../safety_checks';

export const TRANSLATE_KEY_BOOKING_BOOKABILITY_REASON = 'BOOKING_BOOKABILITY_REASON_';

/**
 * Pourquoi une prestation du tunnel n'est pas réservable maintenant (spec Parcours § 7.8, P3), déduit de la
 * configuration et de la sonde existante (aucune sonde en plus). Seules NO_PLANNING et NO_MEMBER sont « Pas
 * réservable en ligne » : une prestation complète (NO_SLOT_IN_HORIZON) a un planning et n'est jamais masquée.
 */
export enum BookingBookabilityReason {
  /** Toutes les formules sont sur devis : bouton « Demander un devis ». */
  QUOTE_ONLY = 'QUOTE_ONLY',
  /** Ni prix ni durée : prestation vitrine, hors du tunnel. */
  SHOWCASE = 'SHOWCASE',
  /** Aucun planning actif ne couvre la prestation. */
  NO_PLANNING = 'NO_PLANNING',
  /** Aucun membre ne peut la réaliser alors qu'elle en demande. */
  NO_MEMBER = 'NO_MEMBER',
  /** Planning ouvert, aucun créneau trouvé par la sonde (complet pour le moment). */
  NO_SLOT_IN_HORIZON = 'NO_SLOT_IN_HORIZON',
  /** Réservable seule, pas avec la sélection en cours (recalcul live). */
  INCOMPATIBLE_WITH_CART = 'INCOMPATIBLE_WITH_CART',
  /** Vue pro seulement (catalogue) : couverte par un planning, avec ses membres. Jamais envoyée au tunnel. */
  BOOKABLE = 'BOOKABLE',
}

export const BookingBookabilityReasonTranslation = {
  'fr-FR': {
    [`${TRANSLATE_KEY_BOOKING_BOOKABILITY_REASON}${BookingBookabilityReason.QUOTE_ONLY}`]: 'Sur devis',
    [`${TRANSLATE_KEY_BOOKING_BOOKABILITY_REASON}${BookingBookabilityReason.SHOWCASE}`]: 'Non réservable en ligne',
    [`${TRANSLATE_KEY_BOOKING_BOOKABILITY_REASON}${BookingBookabilityReason.NO_PLANNING}`]: 'Pas réservable en ligne',
    [`${TRANSLATE_KEY_BOOKING_BOOKABILITY_REASON}${BookingBookabilityReason.NO_MEMBER}`]: 'Pas réservable en ligne',
    [`${TRANSLATE_KEY_BOOKING_BOOKABILITY_REASON}${BookingBookabilityReason.NO_SLOT_IN_HORIZON}`]:
      'Complet pour le moment',
    [`${TRANSLATE_KEY_BOOKING_BOOKABILITY_REASON}${BookingBookabilityReason.INCOMPATIBLE_WITH_CART}`]:
      'Pas de créneau commun avec votre sélection',
    [`${TRANSLATE_KEY_BOOKING_BOOKABILITY_REASON}${BookingBookabilityReason.BOOKABLE}`]: 'Réservable en ligne',
  },
};

validateEnumTranslations(
  BookingBookabilityReason,
  BookingBookabilityReasonTranslation,
  TRANSLATE_KEY_BOOKING_BOOKABILITY_REASON,
  true,
);

/** La raison classe-t-elle la prestation « Pas réservable en ligne » (masquable par l'option du pro) ? */
export const isNotBookableOnline = (reason: string | undefined): boolean =>
  reason === BookingBookabilityReason.NO_PLANNING || reason === BookingBookabilityReason.NO_MEMBER;

/**
 * Réservabilité d'une prestation dans le catalogue du pro (badge, encart de la fiche) : mêmes règles que le
 * tunnel, sans sonde de créneaux. Une prestation complète a un planning : elle reste « Réservable en ligne ».
 */
export const PRO_PRESTATION_BOOKABILITY_STATUSES = [
  BookingBookabilityReason.BOOKABLE,
  BookingBookabilityReason.QUOTE_ONLY,
  BookingBookabilityReason.SHOWCASE,
  BookingBookabilityReason.NO_PLANNING,
  BookingBookabilityReason.NO_MEMBER,
] as const;
export type ProPrestationBookability = (typeof PRO_PRESTATION_BOOKABILITY_STATUSES)[number];

export const TRANSLATE_KEY_PRO_PRESTATION_BOOKABILITY = 'PRO_PRESTATION_BOOKABILITY_';

/** Libellés pro des statuts du catalogue (namespace PROFESSIONAL_APP_CATALOG_PAGE). */
export const ProPrestationBookabilityTranslation = {
  'fr-FR': {
    [`${TRANSLATE_KEY_PRO_PRESTATION_BOOKABILITY}${BookingBookabilityReason.BOOKABLE}`]: 'Réservable en ligne',
    [`${TRANSLATE_KEY_PRO_PRESTATION_BOOKABILITY}${BookingBookabilityReason.QUOTE_ONLY}`]: 'Sur devis',
    [`${TRANSLATE_KEY_PRO_PRESTATION_BOOKABILITY}${BookingBookabilityReason.SHOWCASE}`]: 'Vitrine',
    [`${TRANSLATE_KEY_PRO_PRESTATION_BOOKABILITY}${BookingBookabilityReason.NO_PLANNING}`]:
      'Non réservable : aucun planning',
    [`${TRANSLATE_KEY_PRO_PRESTATION_BOOKABILITY}${BookingBookabilityReason.NO_MEMBER}`]:
      'Non réservable : aucun membre',
  },
};

/**
 * Prestation que le moteur de créneaux peut placer (au moins une formule avec une durée réservable), d'après
 * son statut du catalogue pro ? Statut inconnu (évaluation en échec) : on la garde.
 */
export const isSchedulablePrestation = (bookability: string | undefined): boolean =>
  bookability !== BookingBookabilityReason.QUOTE_ONLY && bookability !== BookingBookabilityReason.SHOWCASE;
